import moment from 'moment';
import { callMintable } from 'api/gearContractApi';
import { getSaleId } from './sale-ids-for-round';
import { gearGrade } from 'config/config';
import env from 'config/env';
import { fetchSaleBoard } from 'api/gearAPI';
import { option, perLogger } from './per-logger';

export const saleState = {
  none: 'NONE',
  wait: 'WAIT',
  sale: 'SALE',
  end: 'END',
  maintenance: 'MAINTENANCE',
};

export const getState = async (startAt, rounds, maintenance) => {
  if (maintenance) {
    console.warn('점검 중입니다.');
    return saleState.maintenance;
  }
  if (!rounds || rounds.length === 0) {
    console.warn('라운드 데이터가 없습니다.');
    return saleState.none;
  }
  if (!startAt) {
    console.warn('판매 시작 데이터가 없습니다.');
    return saleState.none;
  }

  try {
    // 판매가 시작되지 않았으면 판매 전 상태이다.
    const started = _isRoundStarted(startAt);
    if (started === false) {
      return saleState.wait;
    }
  } catch (e) {
    console.error('라운드 시작 시간이 형식에 맞지 않습니다.', startAt, e);
    return saleState.none;
  }

  return saleState.sale;
};

export const getRoundAndRest = async (rounds, address, roundId) => {
  if (!rounds || rounds.length === 0) {
    console.log('라운드 데이터가 없습니다.');
    return { round: undefined, rest: 0 };
  }
  if (!address) {
    console.log('판매 계약 데이터가 없습니다.');
    return { round: undefined, rest: 0 };
  }

  if (env.isLocal) {
    return await _isLocalRoundCheck2(roundId, rounds);
  }

  let id = roundId;
  let rest = 0;
  while (id <= rounds.length) {
    if (id === 0) {
      id++;
      continue;
    }
    const saleId = getSaleId(id, gearGrade.rare);
    try {
      const amount = await callMintable({
        saleId: saleId,
        contractAddress: address,
      });
      if (amount > 0) {
        rest = amount * 10;
        break;
      }
    } catch (e) {
      console.error(`${e.message} [saleID: ${saleId}]`);
    }
    id++;
  }

  // 구입 가능한 라운드가 있으면
  if (id <= rounds.length) {
    perLogger.log(`티어: ${rounds[id - 1].roundId}, 남은 수량: ${rest}`, {
      ignore: [option.prod],
      consoleMode: true,
    });
    return { round: rounds[id - 1], rest: rest };
  }

  // 모든 라운드가 구입 불가능 하다면 판매 종료이다.
  return { round: rounds[rounds.length - 1], rest: 0 };
};

export const getSaleState = async (startAt, rounds, address, roundId) => {
  if (!rounds || rounds.length === 0) {
    console.log('라운드 데이터가 없습니다.');
    return [saleState.none];
  }
  if (!startAt) {
    console.log('판매 시작 데이터가 없습니다.');
    return [saleState.none];
  }
  if (!address) {
    console.log('판매 계약 데이터가 없습니다.');
    return [saleState.none];
  }

  try {
    // 판매가 시작되지 않았으면 판매 전 상태이다.
    const started = _isRoundStarted(startAt);
    if (started === false) {
      return [saleState.wait, rounds[0], rounds[0].amount];
    }
  } catch (e) {
    console.error('라운드 시작 시간이 형식에 맞지 않습니다.', startAt, e);
    return [saleState.none];
  }

  if (env.isLocal) {
    return await _isLocalRoundCheck(roundId, rounds);
  }

  let id = roundId;
  let rest = 0;
  while (id <= rounds.length) {
    if (id === 0) {
      id++;
      continue;
    }
    const saleId = getSaleId(id, gearGrade.rare);
    try {
      const amount = await callMintable({
        saleId: saleId,
        contractAddress: address,
      });
      if (amount > 0) {
        rest = amount * 10;
        break;
      }
    } catch (e) {
      console.error(`${e.message} [saleID: ${saleId}]`);
    }
    id++;
  }

  // 구입 가능한 라운드가 있으면
  if (id <= rounds.length) {
    perLogger.log(`티어: ${rounds[id - 1].roundId}, 남은 수량: ${rest}`, {
      ignore: [option.prod],
      consoleMode: true,
    });
    return [saleState.sale, rounds[id - 1], rest];
  }

  // 모든 라운드가 구입 불가능 하다면 판매 종료이다.
  return [saleState.end, rounds[rounds.length - 1], 0];
};

const _isRoundStarted = (startAt) => {
  const start = moment.utc(startAt, 'YYYY/MM/DD hh:mm');
  const now = moment.utc();
  return now >= start;
};

const _isLocalRoundCheck = async (roundId, rounds) => {
  const res = await fetchSaleBoard();
  let id = roundId;
  let rest = 0;
  while (id <= rounds.length) {
    const saleId = getSaleId(id, gearGrade.rare);
    try {
      const amount = res[saleId.split('g')[0]];
      if (amount > 0) {
        rest = amount * 10;
        break;
      }
    } catch (e) {
      console.error(`${e.message} [saleID: ${saleId}]`);
    }
    id++;
  }

  // 구입 가능한 라운드가 있으면
  if (id <= rounds.length) {
    perLogger.log(`티어: ${rounds[id - 1].roundId}, 남은 수량: ${rest}`, {
      ignore: [option.prod],
      consoleMode: true,
    });
    return [saleState.sale, rounds[id - 1], rest];
  }

  // 모든 라운드가 구입 불가능 하다면 판매 종료이다.
  return [saleState.end, rounds[rounds.length - 1], 0];
};

const _isLocalRoundCheck2 = async (roundId, rounds) => {
  const res = await fetchSaleBoard();
  let id = roundId;
  let rest = 0;
  while (id <= rounds.length) {
    const saleId = getSaleId(id, gearGrade.rare);
    try {
      const amount = res[saleId.split('g')[0]];
      if (amount > 0) {
        rest = amount * 10;
        break;
      }
    } catch (e) {
      console.error(`${e.message} [saleID: ${saleId}]`);
    }
    id++;
  }

  // 구입 가능한 라운드가 있으면
  if (id <= rounds.length) {
    perLogger.log(`티어: ${rounds[id - 1].roundId}, 남은 수량: ${rest}`, {
      ignore: [option.prod],
      consoleMode: true,
    });
    return { round: rounds[id - 1], rest: rest };
  }

  // 모든 라운드가 구입 불가능 하다면 판매 종료이다.
  return { round: rounds[rounds.length - 1], rest: 0 };
};
